import axios from 'axios';
import { safeConsoleLog } from './devToolsDetection';

// Spotify API endpoints
const _SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';
const _SPOTIFY_TOKEN_URL = 'https://accounts.spotify.com/api/token';
const SPOTIFY_API_BASE_URL = 'https://api.spotify.com/v1';

// Add constants for rate limiting and cooldown
const MAX_INIT_ATTEMPTS = 2;
const INIT_COOLDOWN_MS = 60000; // 1 minute
const TOKEN_REFRESH_COOLDOWN_MS = 30000; // 30 seconds

// Spotify API scopes needed for our app
const _SCOPES = [
  'playlist-read-private',
  'playlist-read-collaborative',
  'playlist-modify-public',
  'playlist-modify-private',
  'user-read-email',
  'user-read-private',
  'streaming', // Add streaming scope for Web Playback SDK
  'user-read-playback-state',
  'user-modify-playback-state'
];

// Create a Spotify API client
const spotifyApi = axios.create({
  baseURL: SPOTIFY_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add an interceptor to add the access token to requests
spotifyApi.interceptors.request.use(
  config => {
    const token = localStorage.getItem('spotify_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Handle token refresh when we get a 401 error
spotifyApi.interceptors.response.use(
  response => response,
  async error => {
    // Store only necessary error information to avoid circular references
    const originalRequest = { ...error.config };
    delete originalRequest.transformRequest;
    delete originalRequest.transformResponse;
    delete originalRequest.validateStatus;
    
    // If the error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Check if we're in a cooldown period for token refresh
        const lastRefreshTime = parseInt(localStorage.getItem('spotify_last_refresh_time') || '0');
        const now = Date.now();
        
        if (now - lastRefreshTime < TOKEN_REFRESH_COOLDOWN_MS) {
          localStorage.setItem('spotify_player_fallback', 'true');
          return Promise.reject(new Error('Token refresh in cooldown'));
        }
        
        // Try to refresh the token
        const refreshToken = localStorage.getItem('spotify_refresh_token');
        if (refreshToken) {
          // Update the last refresh time
          localStorage.setItem('spotify_last_refresh_time', now.toString());
          
          const newToken = await refreshSpotifyToken(refreshToken);
          if (newToken) {
            // Update the request with the new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return spotifyApi(originalRequest);
          }
        }
        
        // If we couldn't refresh, redirect to login
        localStorage.setItem('spotify_player_fallback', 'true');
        return Promise.reject(new Error('Failed to refresh token'));
      } catch (refreshError) {
        localStorage.setItem('spotify_player_fallback', 'true');
        return Promise.reject(new Error('Error during token refresh'));
      }
    } else if (error.response?.status === 429) {
      localStorage.setItem('spotify_player_fallback', 'true');
    }
    
    // Use our safe error logging utility to prevent circular references
    const safeError = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message
    };
    
    safeConsoleLog('Spotify API Error:', safeError);
    
    return Promise.reject(error);
  }
);

// Global player instance and state
let player = null;
let isPlayerReady = false;
let playerInitializing = false;
let playerCallbacks = {
  onPlayerStateChanged: null,
  onPlayerReady: null,
  onPlayerError: null
};

// Limit the number of concurrent requests
let pendingRequests = 0;
const MAX_CONCURRENT_REQUESTS = 5;

// Add request throttling
const throttleRequest = () => {
  return new Promise((resolve) => {
    const checkAndProceed = () => {
      if (pendingRequests < MAX_CONCURRENT_REQUESTS) {
        pendingRequests++;
        resolve();
      } else {
        setTimeout(checkAndProceed, 100);
      }
    };
    checkAndProceed();
  });
};

// Release a request slot
const releaseRequestSlot = () => {
  pendingRequests = Math.max(0, pendingRequests - 1);
};

// Modify the original axios request to include throttling
const originalRequest = spotifyApi.request;
spotifyApi.request = async function(...args) {
  try {
    await throttleRequest();
    return await originalRequest.apply(this, args);
  } finally {
    releaseRequestSlot();
  }
};

// Initialize the Spotify Web Playback SDK
export const initializePlayer = (callbacks = {}) => {
  return new Promise((resolve, reject) => {
    // Save callbacks
    if (callbacks.onPlayerStateChanged) playerCallbacks.onPlayerStateChanged = callbacks.onPlayerStateChanged;
    if (callbacks.onPlayerReady) playerCallbacks.onPlayerReady = callbacks.onPlayerReady;
    if (callbacks.onPlayerError) playerCallbacks.onPlayerError = callbacks.onPlayerError;

    // Check if dev tools are open - if so, use fallback mode to prevent crashes
    try {
      if (typeof window !== 'undefined' && 
          window.__REACT_DEVTOOLS_GLOBAL_HOOK__ && 
          window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers && 
          window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers.size > 0) {
        localStorage.setItem('spotify_player_fallback', 'true');
        if (playerCallbacks.onPlayerError) {
          playerCallbacks.onPlayerError('DevTools active: Songs will open in Spotify instead of playing in-app.');
        }
        resolve();
        return;
      }
    } catch (e) {}

    // Check if we're already in fallback mode
    if (shouldUseFallbackMode()) {
      if (playerCallbacks.onPlayerError) {
        playerCallbacks.onPlayerError('Using fallback mode: Songs will open in Spotify instead of playing in-app.');
      }
      resolve();
      return;
    }

    // Check if Spotify SDK script is already loaded
    if (window.Spotify && player) {
      resolve();
      return;
    }

    // Prevent multiple simultaneous initializations
    if (playerInitializing) {
      resolve();
      return;
    }
    
    playerInitializing = true;

    // Track initialization attempts to prevent rate limiting
    const initAttempts = parseInt(localStorage.getItem('spotify_init_attempts') || '0');
    const lastInitTime = parseInt(localStorage.getItem('spotify_last_init_time') || '0');
    const now = Date.now();
    
    // If we've tried to initialize too many times in a short period, use fallback mode
    if (initAttempts >= MAX_INIT_ATTEMPTS && (now - lastInitTime) < INIT_COOLDOWN_MS) {
      if (playerCallbacks.onPlayerError) {
        playerCallbacks.onPlayerError('Rate limited by Spotify. Using fallback mode.');
      }
      localStorage.setItem('spotify_player_fallback', 'true');
      playerInitializing = false;
      resolve(); // Resolve without initializing the player
      return;
    }
    
    // Update initialization tracking
    localStorage.setItem('spotify_init_attempts', (initAttempts + 1).toString());
    localStorage.setItem('spotify_last_init_time', now.toString());

    // Check if we have a valid token before trying to initialize
    const token = localStorage.getItem('spotify_access_token');
    if (!token) {
      localStorage.setItem('spotify_player_fallback', 'true');
      if (playerCallbacks.onPlayerError) {
        playerCallbacks.onPlayerError('No Spotify access token available');
      }
      playerInitializing = false;
      resolve();
      return;
    }

    // Check if script already exists to prevent duplicate loading
    if (document.getElementById('spotify-player-sdk')) {
      playerInitializing = false;
      resolve();
      return;
    }

    // Load the Spotify Web Playback SDK
    const script = document.createElement('script');
    script.id = 'spotify-player-sdk'; // Add ID to prevent duplicate loading
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    
    script.onload = () => {};
    
    script.onerror = (error) => {
      localStorage.setItem('spotify_player_fallback', 'true');
      if (playerCallbacks.onPlayerError) {
        playerCallbacks.onPlayerError('Failed to load Spotify Web Playback SDK');
      }
      playerInitializing = false;
      reject(error);
    };
    
    document.body.appendChild(script);
    
    // Wait for the Spotify Web Playback SDK to be ready
    window.onSpotifyWebPlaybackSDKReady = () => {
      createPlayer();
      playerInitializing = false;
      resolve();
    };
    
    // Set a timeout in case the SDK doesn't initialize properly
    const timeoutId = setTimeout(() => {
      if (!isPlayerReady) {
        if (playerCallbacks.onPlayerError) {
          playerCallbacks.onPlayerError('Initialization timed out. Using fallback mode.');
        }
        localStorage.setItem('spotify_player_fallback', 'true');
        playerInitializing = false;
        resolve(); // Resolve without fully initializing
      }
    }, 15000); // 15 second timeout
    
    // Clean up timeout if component unmounts
    return () => {
      clearTimeout(timeoutId);
      playerInitializing = false;
    };
  });
};

// Create the Spotify player
const createPlayer = () => {
  const token = localStorage.getItem('spotify_access_token');
  
  if (!token) {
    localStorage.setItem('spotify_player_fallback', 'true');
    if (playerCallbacks.onPlayerError) {
      playerCallbacks.onPlayerError('No Spotify access token available');
    }
    return;
  }
  
  // Check if we already have a player instance
  if (player) {
    player.disconnect();
    player = null;
  }
  
  try {
    player = new window.Spotify.Player({
      name: 'Wedding Planner Web Player',
      getOAuthToken: cb => {
        // Provide the current access token
        cb(token);
      },
      volume: 0.5
    });
    
    // Error handling
    player.addListener('initialization_error', ({ message }) => {
      localStorage.setItem('spotify_player_fallback', 'true');
      if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError(message);
    });
    
    player.addListener('authentication_error', ({ _message }) => {
      // Immediately set fallback mode to prevent continuous retries
      localStorage.setItem('spotify_player_fallback', 'true');

      // Check if we're in a cooldown period for token refresh
      const lastRefreshTime = parseInt(localStorage.getItem('spotify_last_refresh_time') || '0');
      const now = Date.now();

      if (now - lastRefreshTime < TOKEN_REFRESH_COOLDOWN_MS) {
        if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Authentication failed');
        return;
      }

      // Try to refresh the token
      const refreshToken = localStorage.getItem('spotify_refresh_token');
      if (refreshToken) {
        // Update the last refresh time
        localStorage.setItem('spotify_last_refresh_time', now.toString());
        
        refreshSpotifyToken(refreshToken)
          .then(() => {
            // We'll stay in fallback mode for now to prevent continuous retries
            // The user can try again later
            if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Authentication refreshed. Please try again later.');
          })
          .catch(_error => {
          if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Authentication failed');
        });
      } else {
        if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Authentication failed');
      }
    });
    
    player.addListener('account_error', ({ message }) => {
      localStorage.setItem('spotify_player_fallback', 'true');

      // Check if this is a premium account error
      if (message.includes('premium') || message === 'This functionality is restricted to premium users only') {
        setUserPremiumStatus(false);
      }

      if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError(message);
    });
    
    player.addListener('playback_error', ({ message }) => {
      if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError(message);
    });
    
    // Playback status updates
    player.addListener('player_state_changed', state => {
      if (playerCallbacks.onPlayerStateChanged) playerCallbacks.onPlayerStateChanged(state);
    });
    
    // Ready
    player.addListener('ready', ({ device_id }) => {
      isPlayerReady = true;
      localStorage.setItem('spotify_player_fallback', 'false');
      // Reset initialization attempts on successful connection
      localStorage.setItem('spotify_init_attempts', '0');
      if (playerCallbacks.onPlayerReady) playerCallbacks.onPlayerReady(device_id);
    });
    
    // Not Ready
    player.addListener('not_ready', ({ _device_id }) => {
      isPlayerReady = false;
    });
    
    // Connect to the player
    player.connect().then(success => {
      if (!success) {
        localStorage.setItem('spotify_player_fallback', 'true');
        if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Failed to connect to Spotify player');
      }
    });
  } catch (error) {
    localStorage.setItem('spotify_player_fallback', 'true');
    if (playerCallbacks.onPlayerError) playerCallbacks.onPlayerError('Failed to create player');
  }
};

// Check if we should use fallback mode
export const shouldUseFallbackMode = () => {
  return localStorage.getItem('spotify_player_fallback') === 'true';
};

// Check if the user has a premium account
export const isUserPremium = () => {
  return localStorage.getItem('spotify_user_premium') === 'true';
};

// Set the user's premium status
export const setUserPremiumStatus = (isPremium) => {
  localStorage.setItem('spotify_user_premium', isPremium ? 'true' : 'false');
};

// Disconnect the player
export const disconnectPlayer = () => {
  if (player) {
    player.disconnect();
    player = null;
  }
};

// Play a track
export const playTrack = async (trackUri, positionMs = 0) => {
  // Check if we're in fallback mode
  if (shouldUseFallbackMode()) {
    // Open the track in Spotify instead
    const trackId = trackUri.split(':').pop();
    window.open(`https://open.spotify.com/track/${trackId}`, '_blank');
    return;
  }
  
  if (!isPlayerReady) {
    throw new Error('Spotify player is not ready');
  }
  
  try {
    await spotifyApi.put('/me/player/play', {
      uris: [trackUri],
      position_ms: positionMs
    });
  } catch (error) {
    // If we get a rate limit error, switch to fallback mode
    if (error.response?.status === 429 || error.response?.status === 403) {
      localStorage.setItem('spotify_player_fallback', 'true');
      
      // Check if this is a premium account error
      if (error.response?.status === 403) {
        setUserPremiumStatus(false);
      }
      
      // Try fallback method
      const trackId = trackUri.split(':').pop();
      window.open(`https://open.spotify.com/track/${trackId}`, '_blank');
    }

    throw error;
  }
};

// Pause playback
export const pausePlayback = async () => {
  // Check if we're in fallback mode
  if (shouldUseFallbackMode()) {
    return; // Can't pause external player
  }
  
  if (!isPlayerReady) {
    throw new Error('Spotify player is not ready');
  }
  
  try {
    await spotifyApi.put('/me/player/pause');
  } catch (error) {
    // If we get a rate limit error, switch to fallback mode
    if (error.response?.status === 429) {
      localStorage.setItem('spotify_player_fallback', 'true');
    }

    throw error;
  }
};

// Resume playback
export const resumePlayback = async () => {
  // Check if we're in fallback mode
  if (shouldUseFallbackMode()) {
    return; // Can't resume external player
  }
  
  if (!isPlayerReady) {
    throw new Error('Spotify player is not ready');
  }
  
  try {
    await spotifyApi.put('/me/player/play');
  } catch (error) {
    // If we get a rate limit error, switch to fallback mode
    if (error.response?.status === 429) {
      localStorage.setItem('spotify_player_fallback', 'true');
    }

    throw error;
  }
};

// Check if the player is ready
export const isPlayerInitialized = () => {
  // If we're in fallback mode, pretend the player is not initialized
  if (shouldUseFallbackMode()) {
    return false;
  }
  
  return isPlayerReady;
};

// Reset fallback mode - can be called when user wants to try again
export const resetFallbackMode = () => {
  localStorage.setItem('spotify_player_fallback', 'false');
  localStorage.setItem('spotify_init_attempts', '0');
  localStorage.setItem('spotify_last_init_time', '0');
  localStorage.setItem('spotify_last_refresh_time', '0');
};

// Get the current playback state
export const getPlaybackState = async () => {
  try {
    const response = await spotifyApi.get('/me/player');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Generate the Spotify authorization URL
export const getSpotifyAuthUrl = (redirectUri) => {
  // Instead of using environment variables, we'll let the backend handle this
  // The backend already has the client ID in its .env file
  return `/api/spotify/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`;
};

// Redirect the user to Spotify login
export const redirectToSpotifyLogin = async () => {
  try {
    // Use the environment variable or fallback
    const redirectUri = process.env.REACT_APP_SPOTIFY_REDIRECT_URI || '/spotify-callback';

    // Get the authorization URL from the backend
    const response = await axios.get(`/api/spotify/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`);

    window.location.href = response.data.authUrl;
  } catch (error) {
    throw error;
  }
};

// Exchange authorization code for access token
export const getSpotifyTokenFromCode = async (code, redirectUri) => {
  try {
    // This request should be made from the backend to protect client secret
    const response = await axios.post('/api/spotify/token', {
      code,
      redirect_uri: redirectUri
    });

    const { access_token, refresh_token, expires_in, spotify_user_id, spotify_display_name } = response.data;

    // Save tokens to localStorage
    localStorage.setItem('spotify_access_token', access_token);
    localStorage.setItem('spotify_refresh_token', refresh_token);
    localStorage.setItem('spotify_token_expiry', Date.now() + (expires_in * 1000));

    // Return the full response data
    return {
      access_token,
      refresh_token,
      expires_in,
      spotify_user_id,
      spotify_display_name
    };
  } catch (error) {
    // Clear any existing tokens
    clearSpotifyTokens();

    throw error;
  }
};

// Refresh the Spotify access token
export const refreshSpotifyToken = async (refreshToken) => {
  try {
    // This request should be made from the backend to protect client secret
    const response = await axios.post('/api/spotify/refresh', {
      refresh_token: refreshToken
    });
    
    const { access_token, expires_in } = response.data;
    
    // Update localStorage
    localStorage.setItem('spotify_access_token', access_token);
    localStorage.setItem('spotify_token_expiry', Date.now() + (expires_in * 1000));
    
    return access_token;
  } catch (error) {
    throw error;
  }
};

// Check if the user is authenticated with Spotify
export const isSpotifyAuthenticated = () => {
  const token = localStorage.getItem('spotify_access_token');
  const expiry = localStorage.getItem('spotify_token_expiry');
  const refreshToken = localStorage.getItem('spotify_refresh_token');
  
  if (!token || !expiry) {
    return false;
  }
  
  // Check if token is expired
  if (Date.now() > parseInt(expiry)) {
    // If we have a refresh token, we'll consider the user still authenticated
    // The actual refresh will happen when needed
    if (!refreshToken) {
      clearSpotifyTokens();
      return false;
    }
    
    // We have a refresh token, so we'll return true and let the API calls
    // handle the refresh when needed
    return true;
  }
  
  return true;
};

// Clear Spotify tokens from localStorage
export const clearSpotifyTokens = () => {
  localStorage.removeItem('spotify_access_token');
  localStorage.removeItem('spotify_refresh_token');
  localStorage.removeItem('spotify_token_expiry');
};

// Get the current user's Spotify profile
export const getSpotifyUserProfile = async () => {
  try {
    const response = await spotifyApi.get('/me');
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get the current user's playlists
export const getUserPlaylists = async () => {
  try {
    const response = await spotifyApi.get('/me/playlists');
    return response.data.items;
  } catch (error) {
    throw error;
  }
};

// Create a new playlist
export const createPlaylist = async (userId, name, description = '', isPublic = false) => {
  try {
    const response = await spotifyApi.post(`/users/${userId}/playlists`, {
      name,
      description,
      public: isPublic
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add tracks to a playlist
export const addTracksToPlaylist = async (playlistId, trackUris) => {
  try {
    const response = await spotifyApi.post(`/playlists/${playlistId}/tracks`, {
      uris: trackUris
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Search for tracks
export const searchTracks = async (query, limit = 20) => {
  try {
    const response = await spotifyApi.get('/search', {
      params: {
        q: query,
        type: 'track',
        limit
      }
    });
    return response.data.tracks.items;
  } catch (error) {
    throw error;
  }
};

// Get a track by its Spotify ID
export const getTrack = async (trackId) => {
  try {
    const response = await spotifyApi.get(`/tracks/${trackId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get a playlist by its Spotify ID
export const getPlaylist = async (playlistId) => {
  try {
    const response = await spotifyApi.get(`/playlists/${playlistId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Extract track ID from Spotify URL
export const extractTrackId = (spotifyUrl) => {
  if (!spotifyUrl) return null;
  
  // Handle different Spotify URL formats
  const trackIdMatch = spotifyUrl.match(/track\/([a-zA-Z0-9]+)/);
  return trackIdMatch ? trackIdMatch[1] : null;
};

// Convert track ID to Spotify URI
export const trackIdToUri = (trackId) => {
  return `spotify:track:${trackId}`;
};

export default spotifyApi;